import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from 'src/app/components/core/_infrastructure/classes/services/global.service';
import { EnumSiNoTodos, FilterSearchingBaseViewModel, Tc } from 'src/app/components/core/_infrastructure/types/common';
import { RecintoService } from '../recinto.service';
import { FilterSearchingTWRecintoViewModel, RecintoItemResultsViewModel } from '../recinto.types';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { catchError, of, tap } from 'rxjs';
import { TwSolicitudCamViewModelBase, equalObjects } from '../../types/solicitudcam-datosbasicos.types';
import { DialogMode, EnumIdsTcTipoSolicitudCam } from '../../types/enum';
import { CampaniasService } from 'src/app/components/core/_infrastructure/classes/services/campanias-global.service';
import { Mixin } from 'ts-mixer';
import { CampaniaBase } from '../../types/campaniaBase';
import { CultivoService } from '../../cultivos/cultivo.service';

@Component({
    selector: 'app-recinto-wrapper',
    templateUrl: './recinto-wrapper.component.html',
    styleUrls: ['./recinto-wrapper.component.scss'],
})
export class RecintoWrapperComponent extends Mixin(ComponentBase, CampaniaBase) implements OnInit, OnChanges {
    @Input() mode: DialogMode;
    @Input() dialogFilter: TwSolicitudCamViewModelBase;
    @Output() selectedCultivos = new EventEmitter<any>();

    visibilitySpinnerExportar = false;
    searching = false;
    result: RecintoItemResultsViewModel | null;
    payload?: FilterSearchingTWRecintoViewModel;
    readonly = false;
    provincias: Tc[];
    municipios: Tc[];
    tiposRecinto: Tc[];

    dialogMode = DialogMode;

    private route = '/campanias/recinto';

    constructor(
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        protected globalService: GlobalService,
        protected campaniasService: CampaniasService,
        private recintoService: RecintoService,
        private cultivoService: CultivoService,
        private router: Router,
        private navMenuService: NavMenuService,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        if (this.mode !== DialogMode.Dialog) {
            this.filtroBase = null;
        }
        this.recoverLastSearch();
        this.getListas();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.dialogFilter?.currentValue && this.dialogFilter) {
            this.filtroBase = { ...this.dialogFilter }; // Pasamos los datos del input al objeto usado por la herencia
        }
    }

    /**
     * We try to get back the previous search done by the user.
     */
    private recoverLastSearch() {
        this.payload = this.recintoService.getLastSearch();
    }

    private getListas(): void {
        this.loadProvincias();
        this.getTiposSolicitud();
        if (this.payload?.IdProvincia) {
            this.onProvinciaSelected(this.payload?.IdProvincia);
        }
    }

    private getTiposSolicitud(): void {
        // Se invoca al servicio para obtener los tipos de recinto, que son los mismos que se usan en los tipos de solicitud de Campaña
        // excepto el tipo Confección, que habrá que sacarlo en ésta funcionalidad, para que no aparezca en el desplegable.
        this.globalService.getTipoSolicitudCam().subscribe((tiposSolicitudCam: Tc[]) => {
            this.tiposRecinto = tiposSolicitudCam?.filter(
                tipoSolicitudCam =>
                    tipoSolicitudCam.Id !== EnumIdsTcTipoSolicitudCam.ID_CONFECCION &&
                    tipoSolicitudCam.Id !== EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION,
            );
        });
    }

    /**
     * Respond to the action of searching on recinto-filter.
     * It will send the filter to the backend and set the response.
     * @param payload FilterSearchingTWRecintoViewModel
     * @return void
     */
    onSearch(payload: FilterSearchingTWRecintoViewModel): void {
        this.navMenuService.clearMenuServiceForRecinto();

        if (!equalObjects(this.payload, payload)) {
            // As soon as the user changes the "filter", the previous payload pagination values have no sense
            // When recovering the request params from the last result, the pagination params won't be reset.
            payload = this.clearPaginationFilter(payload);
        }

        this.payload = { ...payload } as FilterSearchingTWRecintoViewModel; // To keep a copy to be used in the lazy search (paginator)
        if (!payload) {
            return;
        }
        if (this.mode === DialogMode.Dialog) {
            // Ventana de diálogo para añadir cultivos a la solicitud.
            // En éste caso filtramos además por Periodo, para que los recintos-cultivos que aparezcan en pantalla, vengan filtrados además por las variedades configuradas para el periodo.
            // Y que no estén asignados a otras solicitudes (del periodo actual) y no estén en lista negra para la campaña en curso.
            // Se descartan en el listado los recintos/cultivos de la solicitud indicada.
            this.payload.IdPeriodoCam = this.dialogFilter.IdPeriodoCam;
            this.payload.IdTipoRecinto = this.dialogFilter.IdTipoSolicitudCam;
            this.payload.IdCa = this.dialogFilter.IdCa;
            this.payload.IdSolicitudCam = this.dialogFilter.Id;
            this.payload.IdCampania = this.dialogFilter.IdCampania;
            this.payload.Autorizado = EnumSiNoTodos.SI;
        }
        this.searching = true;
        this.result = null;

        if (this.payload.EsCompleta === null) {
            this.payload.EsCompleta = EnumSiNoTodos.TODOS;
        }
        if (this.payload.Autorizado === null) {
            this.payload.Autorizado = EnumSiNoTodos.TODOS;
        }
        // Si estamos en una ventana de selección de recintos-cultivos, consultamos el servicio que ataca a la tabla cultivos.
        if (this.mode === DialogMode.Dialog) {
            this.getRecintosDesdeCultivos(this.payload);
            return;
        }

        // En el listado normal de recintos, consultamos la tabla recintos
        this.recintoService
            .getRecintos(this.payload)
            .pipe(
                tap(results => {
                    this.result = results;
                    this.searching = true;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    private getRecintosDesdeCultivos(payload: FilterSearchingTWRecintoViewModel): void {
        this.cultivoService
            .getRecintosCultivos(payload)
            .pipe(
                tap(results => {
                    this.result = results;
                    this.searching = true;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * We don't clear the whole filter but only the fields related to the pagination.
     */
    private clearPaginationFilter(payload: FilterSearchingTWRecintoViewModel): FilterSearchingTWRecintoViewModel {
        if (!payload) {
            return payload;
        }
        payload.PageNumber = 0;
        payload.Asc = 0;
        payload.OrderBy = '';

        return payload;
    }

    /**
     * It will be invoked when using the paginator
     * @param lazyLoadParams FilterSearchingBaseViewModel
     * @return void
     */
    onSearchLazy(lazyLoadParams: FilterSearchingBaseViewModel): void {
        this.payload = {
            ...this.payload,
            PageNumber: lazyLoadParams.PageNumber,
            Asc: lazyLoadParams.Asc,
            OrderBy: lazyLoadParams.OrderBy,
        } as FilterSearchingTWRecintoViewModel;
        this.onSearch(this.payload);
    }

    /**
     * Invoked when the user wants to clear the form
     * @return void
     */
    onReset(): void {
        this.searching = false;
        this.result = null;
        this.recintoService.resetFilter();
        this.navMenuService.clearMenuServiceForRecinto();
        this.municipios = [];
    }

    /**
     * Redirigimos al formulario de creación de un nuevo recinto
     * @return void
     */
    onAdd(): void {
        // Borramos una posible selección anterior de recinto en el menú
        this.navMenuService.clearMenuServiceForRecinto();
        this.router.navigate([this.route, 'create']);
    }

    /**
     * Redirigimos al formulario de edición del recinto.
     * Cuando el parámetro sea null, el backend captura el error y se muestra en pantalla.
     * @param itemId number|null
     * @return void
     */
    onEdit(itemId: number | null): void {
        this.router.navigate([this.route, 'edit', itemId]);
    }

    /**
     * Invoked when the user wants to export the result to excel
     * @return void
     */
    onExportExcel(): void {
        this.visibilitySpinnerExportar = true;

        let model: FilterSearchingTWRecintoViewModel = new FilterSearchingTWRecintoViewModel();
        Object.assign(model, this.payload);

        this.recintoService.export(model).subscribe(res => {
            this.OpenDownloadedFile(res);
            this.visibilitySpinnerExportar = false;
        });
    }

    /**
     * Recintos-Cultivos seleccionados en el listado de recintos-cultivos
     * @param $event { selection: RecintoItemViewModel[], dblclick: dblclick }
     */
    onSelectedCultivos($event: any): void {
        this.selectedCultivos.emit($event);
    }

    /**
     * To check whether there are results to export
     * @returns boolean
     */
    exportIsAvailable(): boolean {
        return this.result?.ResultList ? this.result?.ResultList?.length > 0 : false;
    }
}
